import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Burger from './burger.jsx';
import MENUS from '../constants/menus';
import LINKS from '../constants/links';
import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion';
import clsx from 'clsx';
import aboutusicon from '../assets/navigation/aboutus.svg';
import blogicon from '../assets/navigation/blog.svg';
import casestudiesicon from '../assets/navigation/casestudies.svg';
import contacticon from '../assets/navigation/contact.svg';
import faqicon from '../assets/navigation/faq.svg';
import insightblockicon from '../assets/navigation/insightblock.svg';
import karriereicon from '../assets/navigation/karriere.svg';
import officeblockicon from '../assets/navigation/officeblock.svg';
import ressourcenicon from '../assets/navigation/ressourcen.svg';
import salesblockicon from '../assets/navigation/salesblock.svg';
import serviceblockicon from '../assets/navigation/serviceblock.svg';
import blckslogo from '../assets/icons/BLCKS_LOGO_NEW.svg';
import arrowdownicon from '../assets/icons/button-arrow-down-white.svg';
import arrowrighticon from '../assets/icons/button-arrow-right-lightgrey.svg';

const ANIMATION_DURATION = 0.2;

const variants = {
  from: {
    opacity: 0,
    translateY: 30,
    transition: {
      duration: ANIMATION_DURATION,
    },
    transitionEnd: {
      zIndex: -1,
    },
  },
  to: {
    zIndex: 39,
    opacity: 1,
    translateY: 0,
    transition: {
      duration: ANIMATION_DURATION,
    },
  },
};

// Custom Button component to match your design
const Button = ({ 
  children, 
  onClick, 
  className = '', 
  size = 'md',
  to,
  href,
  target,
  rel,
  theme = 'primary',
  ...props 
}) => {
  const sizeClasses = {
    xxs: 'px-3 py-1.5 text-xs',
    xs: 'px-4 py-2 text-sm',
    sm: 'px-5 py-2 text-sm',
    md: 'px-6 py-2.5 text-base',
    lg: 'px-7 py-3 text-lg',
  };

  const themeClasses = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    'gray-15-outline': 'border border-gray-15 text-gray-15 hover:bg-gray-15/10',
    'dropdown-item': 'text-white hover:bg-gray-800 text-left w-full', // New theme for dropdown items
  };

  // Extract border radius classes from className to override default
  const borderRadiusClasses = className.match(/rounded-[^\s]+/g) || [];
  const otherClasses = className.replace(/rounded-[^\s]+/g, '').trim();
  
  // Default border radius
  const defaultBorderRadius = 'rounded-lg';
  
  // Use custom border radius if provided, otherwise use default
  const borderRadius = borderRadiusClasses.length > 0 ? borderRadiusClasses.join(' ') : defaultBorderRadius;

  // If it's a react-router link
  if (to) {
    return (
      <Link
        to={to}
        className={`font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${sizeClasses[size]} ${themeClasses[theme] || ''} ${borderRadius} ${otherClasses}`}
        onClick={onClick}
        {...props}
      >
        {children}
      </Link>
    );
  }

  // If it's an external link
  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={`font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${sizeClasses[size]} ${themeClasses[theme] || ''} ${borderRadius} ${otherClasses}`}
        {...props}
      >
        {children}
      </a>
    );
  }

  // Regular button
  return (
    <button
      className={`font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${sizeClasses[size]} ${themeClasses[theme] || ''} ${borderRadius} ${otherClasses}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Custom Link component to match your design
const LinkComponent = ({
  className = '',
  href,
  to,
  children,
  isExternal = false,
  theme = 'primary',
  withArrow = false,
  tagName,
  handleClick,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center text-green-400 hover:text-green-300 transition-colors duration-200';
  const themeClasses = {
    primary: 'text-green-400 hover:text-green-300',
    white: 'text-white hover:text-gray-200',
    underline: 'text-green-400 hover:text-green-300 underline underline-offset-4',
    'dropdown-item': 'text-white hover:text-gray-300 text-left w-full', // New theme for dropdown items
  };

  const classes = clsx(
    baseClasses,
    themeClasses[theme],
    withArrow && 'group inline-flex items-center gap-1',
    className
  );

  // If it's a react-router link
  if (to && !isExternal) {
    return (
      <Link
        to={to}
        className={classes}
        onClick={handleClick}
        {...props}
      >
        {children}
        {withArrow && (
          <span className="ml-1 transition-transform duration-200 group-hover:translate-x-1">
            →
          </span>
        )}
      </Link>
    );
  }

  // Regular anchor link
  return (
    <a
      href={href || to}
      className={classes}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      {...props}
    >
      {children}
      {withArrow && (
        <span className="ml-1 transition-transform duration-200 group-hover:translate-x-1">
          →
        </span>
      )}
    </a>
  );
};

const MobileMenuItem = ({ text, to, sections, closeMenu, ...otherProps }) => {
  const [isMenuItemOpen, setIsMenuItemOpen] = useState(false);
  const hasSubmenu = sections?.length > 0;

  const handleMenuItemClick = (e) => {
    if (hasSubmenu) {
      // Toggle dropdown for items with sections
      setIsMenuItemOpen(!isMenuItemOpen);
      e.preventDefault();
    } else if (to && to.startsWith('#')) {
      // Handle anchor links
      e.preventDefault();
      if (to === '#consulting') {
        // Match the desktop navigation behavior by using the same selector
        const element = document.querySelector('[data-section="consulting"]');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          // Update URL without page reload
          window.history.pushState(null, '', '/#consulting');
          closeMenu();
        }
      } else {
        const element = document.getElementById(to.replace('#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          closeMenu();
        }
      }
    } else if (to && !hasSubmenu) {
      // For regular links, just close the menu
      closeMenu();
    }
  };

  // For dropdown menu items
  const handleDropdownItemClick = (to, e) => {
    e.preventDefault();
    closeMenu();
    
    // Handle navigation based on the link type
    if (to.startsWith('#')) {
      // Anchor links
      const element = document.getElementById(to.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (to === '/faq') {
      window.location.hash = '';
      window.location.pathname = '/faq';
    } else if (to === '/about') {
      window.location.hash = '';
      window.location.pathname = '/about';
    } else if (to === '/contact') {
      window.location.hash = '';
      window.location.pathname = '/contact';
    } else if (to === '/pricing') {
      window.location.hash = '';
      window.location.pathname = '/pricing';
    } else if (to === '/') {
      // For "Coming Soon" links, just close the menu
      // In a real implementation, you might want to show a message
    } else {
      // For other links, navigate normally
      window.location.href = to;
    }
  };

  return (
    <li className="block shrink-0 overflow-hidden border-b border-[rgba(22,24,29,1)] leading-none">
      {hasSubmenu ? (
        // Dropdown menu item
        <>
          <button
            className="relative flex w-full items-center justify-between py-4 font-normal leading-none tracking-[0.01em] transition-colors duration-200 text-white hover:text-gray-300"
            onClick={handleMenuItemClick}
          >
            <span>{text}</span>
            <img 
              src={arrowdownicon} 
              alt="" 
              className={`w-4 h-4 transition-transform duration-200 ${isMenuItemOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {isMenuItemOpen && (
            <div className="pb-4">
              {sections.map(({ title, items }, index) => (
                <div key={index} className="relative">
                  <div className="mb-4 text-[12px] leading-none tracking-[0.01em]" style={{ color: 'rgba(120, 124, 135, 1)' }}>
                    {title}
                  </div>
                  <ul className="flex flex-col gap-5">
                    {items.map(({ title, description, to }, itemIndex) => (
                      <li className="h-[34px]" key={itemIndex}>
                        <a 
                          href={to}
                          onClick={(e) => handleDropdownItemClick(to, e)}
                          className="group/item relative flex items-center gap-2.5 pr-[26px] hover:bg-[#16181c] rounded-lg p-2 -mx-2 cursor-pointer"
                        >
                          <div className="relative flex h-[34px] w-[34px] items-center justify-center rounded-lg" style={{ backgroundColor: 'rgb(22,24,29,1)', border: '1px solid rgb(46,48,56,1)' }}>
                            {/* Use appropriate icons based on the title */}
                            {title === 'Sales Block' && (
                              <img src={salesblockicon} alt="" className="h-[18px] w-[18px]" />
                            )}
                            {title === 'Office Block' && (
                              <img src={officeblockicon} alt="" className="h-[18px] w-[18px]" />
                            )}
                            {title === 'Insight Block' && (
                              <img src={insightblockicon} alt="" className="h-[18px] w-[18px]" />
                            )}
                            {title === 'Service Block' && (
                              <img src={serviceblockicon} alt="" className="h-[18px] w-[18px]" />
                            )}
                            {title === 'FAQ' && (
                              <img src={faqicon} alt="" className="h-[18px] w-[18px]" />
                            )}
                            {title === 'Blog & Artikel' && (
                              <img src={blogicon} alt="" className="h-[18px] w-[18px]" />
                            )}
                            {title === 'Fallstudien' && (
                              <img src={casestudiesicon} alt="" className="h-[18px] w-[18px]" />
                            )}
                            {title === 'Ressourcen' && (
                              <img src={ressourcenicon} alt="" className="h-[18px] w-[18px]" />
                            )}
                            {title === 'Über uns' && (
                              <img src={aboutusicon} alt="" className="h-[18px] w-[18px]" />
                            )}
                            {title === 'Karriere' && (
                              <img src={karriereicon} alt="" className="h-[18px] w-[18px]" />
                            )}
                            {title === 'Kontakt' && (
                              <img src={contacticon} alt="" className="h-[18px] w-[18px]" />
                            )}
                          </div>
                          <div className="relative min-w-px">
                            <div className="flex items-center">
                              <div className="truncate leading-dense transition-colors duration-100 group-hover/item:text-blue-60 tracking-[0.01em]" style={{ color: 'rgba(239, 239, 241, 1)', fontSize: '14px' }}>
                                {title}
                              </div>
                            </div>
                            <div className="truncate font-light leading-dense transition-colors duration-100 group-hover/item:text-gray-60 mt-1 tracking-[0.01em]" style={{ color: 'rgba(120, 124, 135, 1)', fontSize: '12px' }}>
                              {description}
                            </div>
                          </div>
                          <img src={arrowrighticon} alt="" className="absolute right-2 opacity-0 group-hover/item:opacity-100 transition-opacity h-4 w-4" style={{ color: 'rgba(239, 239, 241, 1)' }} />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        // Regular menu item
        <LinkComponent
          className="relative flex w-full items-center py-4 font-normal leading-none tracking-tight transition-colors duration-200 text-white hover:text-gray-300"
          to={to}
          handleClick={handleMenuItemClick}
          {...otherProps}
        >
          <span>{text}</span>
        </LinkComponent>
      )}
    </li>
  );
};

const mobileMenuItems = [
  // Remove the Home button and use all other items from MENUS.header
  ...MENUS.header.filter(item => item.text !== 'Home'),
];

const NavigationMobile = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        closeMobileMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent background scrolling when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Prevent background scrolling on both html and body
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      // Add padding to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      // Restore scrolling
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    // Cleanup on unmount
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isMobileMenuOpen]);
  
  return (
    <>
      <nav className="fixed top-0 left-0 w-full py-4 z-50 md:hidden">
        <div className="w-full h-12 px-4 flex items-center justify-between">
          {/* Logo - Links to Home */}
          <Link to="/" className="flex-shrink-0" onClick={() => { closeMobileMenu(); }}>
            <img src={blckslogo} alt="BLCKS" width={70} height={20} />
          </Link>
          
          {/* Burger Menu */}
          <Burger 
            className="relative flex text-white"
            isNewDesign
            onClick={toggleMobileMenu}
          />
        </div>
      </nav>
      
      {/* Mobile Menu */}
      <LazyMotion features={domAnimation}>
        <AnimatePresence>
          {isMobileMenuOpen && (
            <m.nav
              className="fixed inset-0 z-40 flex flex-col justify-between bg-[rgba(11,12,15,0.99)] md:hidden"
              initial="from"
              animate="to"
              exit="from"
              variants={variants}
            >
              <div className="flex flex-col h-full">
                <div className="pt-[102px] pb-8 px-8">
                  <ul className="flex flex-col">
                    {mobileMenuItems.map((item, index) => (
                      <MobileMenuItem key={index} {...item} closeMenu={closeMobileMenu} />
                    ))}
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-x-5 gap-y-3.5 p-8 bg-[rgba(11,12,15,0.99)]">
                  <Button
                    className="h-11 flex items-center justify-center text-[14px] font-normal tracking-[0.01em] border border-[rgba(22,24,29,1)] text-white rounded-[9999px]"
                    to="/contact"
                    theme="gray-15-outline"
                    tagName="MobileMenu"
                    onClick={closeMobileMenu}
                  >
                    Kontakt
                  </Button>
                  <Button
                    className="h-11 flex items-center justify-center text-[14px] font-normal tracking-[0.01em] bg-white text-black rounded-[9999px]"
                    href="https://audit.blcks.at"
                    target="_blank"
                    rel="noopener noreferrer"
                    theme="gray-15-outline"
                    tagName="MobileMenu"
                  >
                    Analyse starten
                  </Button>
                </div>
              </div>
            </m.nav>
          )}
        </AnimatePresence>
      </LazyMotion>
    </>
  );
};

export default NavigationMobile;